<template>
  <div class="ledger-page">
    <b-card class="rounded-lg">
      <b-link
        :to="{ name: 'me-payslips' }"
        class="d-inline-block mb-1"
      >
        {{ $t('ledger_payslip_back') }}
      </b-link>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <template v-else-if="slip">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4 class="mb-0">
            {{ periodLabel }}
          </h4>
          <b-badge :variant="statusBadgeVariant(statusValue)">
            {{ statusText }}
          </b-badge>
        </div>

        <div
          v-if="!line"
          class="text-muted"
        >
          {{ $t('ledger_payslip_no_line') }}
        </div>

        <dl
          v-else
          class="ledger-payslip-lines mb-0"
        >
          <div>
            <dt>{{ $t('ledger_payroll_basic') }}</dt>
            <dd class="ledger-num">
              {{ money(line.basic_salary) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('ledger_payroll_allowances') }}</dt>
            <dd class="ledger-num">
              {{ money(line.allowances) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('ledger_payroll_deduction') }}</dt>
            <dd class="ledger-num">
              {{ money(line.employee_deduction) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('ledger_payroll_employer_si') }}</dt>
            <dd class="ledger-num">
              {{ money(line.employer_si) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('ledger_payroll_gross') }}</dt>
            <dd class="ledger-num">
              {{ money(line.gross) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('ledger_payroll_net') }}</dt>
            <dd class="ledger-num font-weight-bold">
              {{ money(line.net) }}
            </dd>
          </div>
        </dl>
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BLink, BBadge, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getMyPayslip } from '@/api/me/payslips'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'MyPayslipShow',
  components: {
    BCard, BLink, BBadge, BSpinner,
  },
  data() {
    return {
      loading: false,
      slip: null,
    }
  },
  computed: {
    statusValue() {
      return enumValue(this.slip && this.slip.status)
    },
    statusText() {
      return enumLabel(this.slip && this.slip.status, this.$i18n.locale)
    },
    line() {
      const { slip } = this
      if (!slip) return null
      if (slip.line && typeof slip.line === 'object') return slip.line
      const lines = Array.isArray(slip.lines) ? slip.lines : []
      if (lines.length === 1) return lines[0]
      if (slip.net != null || slip.gross != null || slip.basic_salary != null) return slip
      return null
    },
    periodLabel() {
      if (!this.slip) return ''
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const month = new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, this.slip.month - 1, 1))
      return `${month} ${this.slip.year}`
    },
  },
  watch: {
    '$route.params.id': 'loadSlip',
  },
  mounted() {
    this.loadSlip()
  },
  methods: {
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    async loadSlip() {
      this.loading = true
      try {
        const res = await getMyPayslip(this.$route.params.id)
        this.slip = oneFrom(res)
      } catch (error) {
        this.slip = null
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
}
.ledger-payslip-lines {
  display: grid;
  gap: 0.75rem;
}
.ledger-payslip-lines dt {
  color: #6e6b7b;
  font-weight: 400;
}
.ledger-payslip-lines dd {
  margin-bottom: 0;
}
</style>
